import { createContext, useContext } from "react";

/**
 * The Flipper flag that releases the fluid frame to first-party agents. The
 * specs import it too.
 */
export const ADAPTIVE_DASHBOARD_FEATURE = "fpa_adaptive_dashboard";

/** The fluid frame's gutters from 1024px, shared by header, content, footer. */
export const FLUID_GUTTER = { maxWidth: "none", px: "clamp(24px, 2vw, 40px)" };

/** The fluid frame's rail width from 1024px. */
export const FLUID_RAIL_WIDTH = "clamp(220px, 10vw, 260px)";

/**
 * The widest a form or record gets in the fluid frame, in px: it starts on
 * the content edge and keeps a readable column rather than run across a pane
 * that grows with the window.
 */
export const FLUID_READABLE_WIDTH = 800;

/**
 * LegacyContainer props for a page that fills the fluid frame's pane: the
 * frame supplies the gutter, and the centred 1100-1450px cap is dropped.
 */
export const FLUID_LEGACY_CONTAINER = {
  variant: "noGutter" as const,
  css: { maxWidth: "none", "@bp5": { maxWidth: "none" } },
};

export interface DashboardFrame {
  /**
   * The page fills the frame and adapts to its width rather than sit in the
   * capped 1920px container. Only the first-party agent dashboard sets it,
   * while ADAPTIVE_DASHBOARD_FEATURE is on. The client and TPA Jobs boards
   * draw a fluid frame for their board alone, so it stays false there.
   */
  fluid: boolean;
}

export const DashboardFrameContext = createContext<DashboardFrame>({
  fluid: false,
});

/**
 * The frame the surrounding DashboardPage draws. Outside one it is not fluid,
 * so a shared component that reads it keeps its default layout.
 */
export const useDashboardFrame = () => useContext(DashboardFrameContext);
